import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Key, Languages, MonitorSmartphone, Shield, Smartphone } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useSendEmailOtp } from "../../application/hooks/useSendEmailOtp";
import { useOtpPreference } from "../../application/hooks/useOtpPreference";
import { useSession } from "../../application/hooks/useSession";
import { DataEncryptionControl } from "../components/DataEncryptionControl";
import { ManageDevicesDialog } from "@/features/sessions/presentation/components/ManageDevicesDialog";
import {
  AUTH_FLOW_PURPOSE_KEY,
  AUTH_FLOW_PURPOSE_RESET_TOTP,
} from "../../domain/constants/authFlowConstants";
import {
  DEFAULT_OTP_PREFERENCE,
  OTP_PREFERENCE_EMAIL,
  OTP_PREFERENCE_TOTP,
  OTP_PREFERENCE_LABELS,
  type OtpPreference,
} from "../../domain/constants/otpConstants";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetMeQuery } from "@/features/profile/infrastructure/api/ProfileApi";
import { useGetLanguagesQuery } from "@/features/languages/infrastructure/api/languagesApi";
import {
  useGetUserSettingsQuery,
  useUpdateUserSettingsMutation,
} from "@/features/user-settings/infrastructure/api/userSettingsApi";
import { LANGUAGE_STORAGE_KEY } from "@/core/i18n/config";
import { cn } from "@/lib/utils";
import { PageShell } from "@/components/layout/PageShell";

interface SettingRowProps {
  icon: ReactNode;
  title: string;
  description: string;
  action: ReactNode;
}

function SettingRow({ icon, title, description, action }: SettingRowProps) {
  return (
    <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:gap-6">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center text-gray-500 [&_svg]:h-6 [&_svg]:w-6">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <p className="mt-1 text-sm leading-relaxed text-gray-500">
          {description}
        </p>
      </div>
      <div className="shrink-0 sm:ml-auto">{action}</div>
    </div>
  );
}

const outlineActionClass =
  "min-w-[110px] border-primary text-primary hover:bg-[#EAF2FF] hover:text-primary";

const SettingsPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("settings");
  const { data: profile, refetch } = useGetMeQuery();
  const { sendEmailOtp, loading: sendingOtp } = useSendEmailOtp();
  const {
    setOtpPreference,
    loading: savingPreference,
    error: preferenceError,
    setError: setPreferenceError,
  } = useOtpPreference();
  const { getUserEmail, getIsTotpEnabled } = useSession();

  const { data: languages = [], isLoading: loadingLanguages } =
    useGetLanguagesQuery();
  const { data: userSettings } = useGetUserSettingsQuery();
  const [updateSettings, { isLoading: savingLanguage }] =
    useUpdateUserSettingsMutation();
  const [languageError, setLanguageError] = useState<string | null>(null);

  const [totpActionError, setTotpActionError] = useState<string | null>(null);
  const [devicesDialogOpen, setDevicesDialogOpen] = useState(false);
  const [otpPreference, setOtpPreferenceState] = useState<OtpPreference>(
    DEFAULT_OTP_PREFERENCE,
  );

  useEffect(() => {
    if (profile?.otpPreference) {
      setOtpPreferenceState(profile.otpPreference);
    }
  }, [profile?.otpPreference]);

  const selectedLanguageId =
    userSettings?.webLanguage?.id ??
    languages.find((lang) => lang.code === i18n.language)?.id ??
    "";

  const handleLanguageChange = async (languageId: string) => {
    const language = languages.find((lang) => lang.id === languageId);
    if (!language) return;

    setLanguageError(null);
    void i18n.changeLanguage(language.code);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language.code);

    try {
      await updateSettings({ web_language: { id: language.id } }).unwrap();
    } catch {
      setLanguageError(t("language.updateError"));
    }
  };

  const totpEnabled = profile?.totpEnabled ?? getIsTotpEnabled() ?? false;

  const handleOtpPreferenceChange = async (preference: OtpPreference) => {
    if (preference === otpPreference) return;

    setPreferenceError(null);
    setTotpActionError(null);

    const result = await setOtpPreference(preference, totpEnabled);

    if ("needsTotpSetup" in result && result.needsTotpSetup) {
      navigate("/scan-qr");
      return;
    }

    if (result.ok) {
      setOtpPreferenceState(preference);
      void refetch();
    }
  };

  const handleTotpAction = async () => {
    setTotpActionError(null);

    if (!totpEnabled) {
      navigate("/scan-qr");
      return;
    }

    const email = profile?.email ?? getUserEmail();
    if (!email) {
      setTotpActionError(t("twoFactor.emailMissingError"));
      return;
    }

    try {
      sessionStorage.setItem(AUTH_FLOW_PURPOSE_KEY, AUTH_FLOW_PURPOSE_RESET_TOTP);
      await sendEmailOtp(email, AUTH_FLOW_PURPOSE_RESET_TOTP);
      navigate("/verify-email-otp", {
        state: { email, purpose: AUTH_FLOW_PURPOSE_RESET_TOTP },
      });
    } catch {
      setTotpActionError(t("twoFactor.sendError"));
    }
  };

  return (
    <PageShell>
      <h1 className="text-xl font-semibold text-gray-900">
        {t("pageTitle")}
      </h1>

      <div className="mx-auto mt-6 max-w-3xl space-y-4">
        <SettingRow
          icon={<Languages />}
          title={t("language.title")}
          description={t("language.description")}
          action={
            <div className="flex flex-col items-end gap-1">
              <Select
                value={selectedLanguageId}
                onValueChange={(value) => void handleLanguageChange(value)}
                disabled={loadingLanguages || savingLanguage}
              >
                <SelectTrigger
                  className={cn("w-[160px] bg-white", outlineActionClass)}
                >
                  <SelectValue placeholder={t("language.placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((lang) => (
                    <SelectItem key={lang.id} value={lang.id}>
                      {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {languageError && (
                <p className="max-w-[220px] text-right text-xs text-red-600">
                  {languageError}
                </p>
              )}
            </div>
          }
        />

        <SettingRow
          icon={<Key />}
          title={t("password.title")}
          description={t("password.description")}
          action={
            <Button
              type="button"
              variant="outline"
              className={outlineActionClass}
              onClick={() =>
                navigate("/forgot-password", {
                  state: { changePasswordContext: true },
                })
              }
            >
              {t("password.action")}
            </Button>
          }
        />

        <SettingRow
          icon={<Smartphone />}
          title={t("signInMethod.title")}
          description={t("signInMethod.description")}
          action={
            <div className="flex flex-col items-end gap-1">
              <Select
                value={otpPreference}
                onValueChange={(value) =>
                  void handleOtpPreferenceChange(value as OtpPreference)
                }
                disabled={savingPreference}
              >
                <SelectTrigger
                  className={cn("w-[220px] bg-white", outlineActionClass)}
                >
                  <SelectValue placeholder="Sign-in method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={OTP_PREFERENCE_TOTP}>
                    {OTP_PREFERENCE_LABELS[OTP_PREFERENCE_TOTP]}
                  </SelectItem>
                  <SelectItem value={OTP_PREFERENCE_EMAIL}>
                    {OTP_PREFERENCE_LABELS[OTP_PREFERENCE_EMAIL]}
                  </SelectItem>
                </SelectContent>
              </Select>
              {preferenceError && (
                <p className="max-w-[220px] text-right text-xs text-red-600">
                  {preferenceError}
                </p>
              )}
            </div>
          }
        />

        <SettingRow
          icon={<Shield />}
          title={
            totpEnabled
              ? t("twoFactor.titleEnabled")
              : t("twoFactor.titleDisabled")
          }
          description={
            totpEnabled
              ? t("twoFactor.descriptionEnabled")
              : t("twoFactor.descriptionDisabled")
          }
          action={
            <div className="flex flex-col items-end gap-1">
              <Button
                type="button"
                variant="outline"
                className={outlineActionClass}
                disabled={sendingOtp}
                onClick={() => void handleTotpAction()}
              >
                {sendingOtp
                  ? t("twoFactor.actionSending")
                  : totpEnabled
                    ? t("twoFactor.actionReconfigure")
                    : t("twoFactor.actionSetUp")}
              </Button>
              {totpActionError && (
                <p className="max-w-[200px] text-right text-xs text-red-600">
                  {totpActionError}
                </p>
              )}
            </div>
          }
        />

        <SettingRow
          icon={<MonitorSmartphone />}
          title={t("devices.title")}
          description={t("devices.description")}
          action={
            <Button
              type="button"
              variant="outline"
              className={outlineActionClass}
              onClick={() => setDevicesDialogOpen(true)}
            >
              {t("devices.action")}
            </Button>
          }
        />

        <DataEncryptionControl />
      </div>

      <ManageDevicesDialog
        open={devicesDialogOpen}
        onOpenChange={setDevicesDialogOpen}
      />
    </PageShell>
  );
};

export default SettingsPage;
